<template>
  <v-card class="pa-2 mb-2 elevation-4" height="100%">
    <v-row>
      <v-col cols="5">
        <v-row class="ml-4">
          <b>Thrust pattern:</b>
          <exo-help-dialog title="Thrust pattern" margin="ml-5">
              The thrust pattern represents a full cycle of the manoeuvre.
              The period of a pattern is defined as the sum of the number of
              thrust orbits and of the number of rest orbits:
              <ul>
                <li>
                  "Thrust orbits" represents the number of consecutive
                  orbits on which burns will be performed.
                </li>
                <li>
                  "Rest orbits" represents the number of consecutive orbits
                  on which no burn will be performed.
                </li>
              </ul>
              The pattern displays the size of thrust arcs on the thrust orbits, taking into account the strategy's duty cycle.
            </exo-help-dialog>
        </v-row>
        <v-row class="mt-5 ml-8" v-if="currentRow != -1">
          <thrustPattern
            :tp_thrust="this.currentNumberOfThrustOrbits"
            :tp_rest="this.currentNumberOfRestOrbits"
            :tp_shift= 0
            :tp_arc= "'TWO'"
            :tp_duty_cycle="this.currentDutyCycle"
          >
          </thrustPattern>
        </v-row>
        <v-row class="mt-5 ml-8">
          <exo-form-alert class="mb-2" type="info">
            To display the thrust pattern of a strategy, click on the eye in the table below.
          </exo-form-alert>
        </v-row>
      </v-col>
      <v-col cols="5">
        <v-row>
        <h5><b>Strategy</b> : {{ strategyGroup.title }}</h5>
        </v-row>
        <v-row align="center" no-gutters class="mt-3">
            <v-checkbox
              v-model="showPlotingArea"
              class="my-0 py-0 mr-0"
              label="Show heatmaps"
            ></v-checkbox>
            <exo-help-dialog title="Heatmaps" margin="mb-5 ml-16">
              The heatmaps display the manoeuvre characteristics depending on the change of altitude and inclination. The four characteristics showcased are the following:
                  <ul>
                    <li>
                      ΔV (m/s)
                    </li>
                    <li>
                      Thrust duration (hours)
                    </li>
                    <li>
                      Manoeuvre duration (days)
                    </li>
                    <li>
                      Number of burns
                    </li>
                  </ul>
                </exo-help-dialog>
        </v-row>
      </v-col>
    </v-row>
    <v-row>
      <v-data-table
        dense
        hide-default-footer
        :headers="headers"
        :items="strategyGroup.strategies"
        class="ml-5"
      >
        <template slot="headers" slot-scope="props">
          <tr>
            <th class="px-2" v-for="header in headers" :key="header.value">
              {{ header.text }}
            </th>
          </tr>
        </template>

        <template slot="item" slot-scope="row">
          <tr class="pa-0">
            <td
              class="px-1 text-right"
              v-for="header in headers"
              :key="header.value"
              :style="rowStyle(row.index)"
            >
              <div v-if="header.value !== 'Display strategy'" class="text-center">
                {{ row.item.parameters[header.value] }}
              </div>
              <div v-if="header.value === 'Display strategy'" class="text-center">
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-btn
                      icon
                      class="mx-0"
                      @click.stop="displayStrategy(row.item, row.index)"
                      v-on="on"
                    >
                      <v-icon>mdi-anchor</v-icon>
                      <icon scale="1" name="eye" />
                    </v-btn>
                  </template>
                  <span>Display strategy</span>
                </v-tooltip>
              </div>
            </td>
          </tr>
        </template>
      </v-data-table>
    </v-row>
    <v-row v-show="showHeatmaps">
      <div :id="'plotingArea-dv-img' + index"></div>
      <div :id="'plotingArea-thrust-img' + index"></div>
      <div :id="'plotingArea-man-img' + index"></div>
      <div :id="'plotingArea-count-img' + index"></div>
    </v-row>
  </v-card>
</template>

<script>
import Plotly from 'plotly.js-dist'
import thrustPattern from '@/components/Forms/ThrustPattern.vue'

export default {
  name: 'strategy-card',

  components: {
    thrustPattern
  },

  props: {
    strategyGroup: {
      type: Object,
      required: true
    },
    index: {
      required: true
    }
  },

  mounted() {},

  data() {
    return {
      currentRow: -1,
      currentStrategy: null,
      currentDutyCycle: 0,
      currentNumberOfThrustOrbits: 1,
      currentNumberOfRestOrbits: 0,
      showPlotingArea: false
    }
  },

  computed: {
    headers() {
      let headers = []
      for (let index in this.strategyGroup.fieldLabels) {
        headers.push({
          text: this.strategyGroup.fieldLabels[index],
          value: this.strategyGroup.fieldNames[index],
          sortable: false
        })
      }
      headers.push({ text: 'Display strategy', value: 'Display strategy', sortable: false })
      return headers
    },

    showHeatmaps() {
      if (this.showPlotingArea && this.currentRow != -1) {
        return true
      }
      return false
    }

  },

  methods: {
    displayStrategy(strategy, rowIndex) {
      this.currentRow = rowIndex
      this.currentStrategy = strategy
      
      this.showPattern()
      this.drawStrategyUsage(strategy)
      
    },

    showPattern() {
        this.currentDutyCycle = parseInt(this.strategyGroup.strategies[this.currentRow].hiddenParameters.dutyCycle * 100)
        this.currentNumberOfThrustOrbits = this.strategyGroup.strategies[this.currentRow].hiddenParameters.thrustOrbitsCount
        this.currentNumberOfRestOrbits = this.strategyGroup.strategies[this.currentRow].hiddenParameters.restOrbits
    },

    drawStrategyUsage(strategy) {
        for (let title of strategy.labelsZ) {
          let data = [
            {
              z: strategy.z[title],
              x: strategy.x,
              y: strategy.y,
              type: 'heatmap',
              connectgaps: false,
              colorbar: {
                tickfont: {
                  color: this.$vuetify.theme.dark ? 'white' : '#121212'
              }
            }
          }
        ]
        this.drawWithData(data, title)
      }
    },

    drawWithData(data, title) {
      let divId = ''
      if (title === 'ΔV (m/s)') {
        divId = 'dv-img'
      } else if (title === 'Manoeuvre duration (days)') {
        divId = 'man-img'
      } else if (title === 'Thrust duration (hours)') {
        divId = 'thrust-img'
      } else if (title === 'Number of burns') {
        divId = 'count-img'
      } else {
        console.log('Unknown result ' + title + ', not able to plot')
        return
      }

      let layout = {
        title: {
          text: title,
          font: {
            color: this.$vuetify.theme.dark ? 'white' : '#121212'
          }
        },
        xaxis: {
          title: this.strategyGroup.strategies[0].labelX,
          titlefont: {
            color: this.$vuetify.theme.dark ? 'white' : '#121212'
          },
          tickfont: {
            color: this.$vuetify.theme.dark ? 'white' : '#121212'
          }
        },
        yaxis: {
          title: this.strategyGroup.strategies[0].labelY,
          titlefont: {
            color: this.$vuetify.theme.dark ? 'white' : '#121212'
          },
          tickfont: {
            color: this.$vuetify.theme.dark ? 'white' : '#121212'
          }
        },
        plot_bgcolor: this.$vuetify.theme.dark ? '#121212' : 'white',
        paper_bgcolor: this.$vuetify.theme.dark ? '#121212' : 'white',
        autosize: false,
        width: 250,
        height: 300
      }
      let plotingAreaName = 'plotingArea-' + divId + this.index
      try {
        Plotly.purge(plotingAreaName)
      } catch (error) {}
      Plotly.plot(plotingAreaName, data, layout)
    },

    rowStyle(rowIndex) {
      return this.currentRow === rowIndex ? 'background-color: #404040;' : ''
    }
  }
}
</script>
